import { rankItem, type RankingInfo } from "@tanstack/match-sorter-utils";
import {
  filterFn_arrIncludes,
  filterFn_equals,
  filterFn_equalsString,
  filterFn_greaterThan,
  filterFn_greaterThanOrEqualTo,
  filterFn_includesString,
  filterFn_lessThan,
  filterFn_lessThanOrEqualTo,
  type ColumnFilter,
  type FilterFn,
  type Row,
  type RowData,
  type TableFeatures,
} from "@tanstack/react-table";

export type FilterOperator =
  | "includesString"
  | "notIncludesString"
  | "equalsString"
  | "notEqualsString"
  | "equals"
  | "notEquals"
  | "greaterThan"
  | "greaterThanOrEqualTo"
  | "lessThan"
  | "lessThanOrEqualTo"
  | "isRelativeToToday"
  | "inRange"
  | "startsWith"
  | "endsWith"
  | "isEmpty"
  | "isNotEmpty";

export type JoinOperator = "and" | "or";

export interface ExtendedColumnFilter extends ColumnFilter {
  filterId?: string;
  operator?: FilterOperator;
  joinOperator?: JoinOperator;
}

export function getFilterOperators(variant: "text" | "number" | "date" | "boolean" | "select" | "multi-select" = "text") {
  switch (variant) {
    case "number":
      return [
        { value: "equals", label: "equals" },
        { value: "notEquals", label: "does not equal" },
        { value: "greaterThan", label: "greater than" },
        { value: "greaterThanOrEqualTo", label: "greater than or equal to" },
        { value: "lessThan", label: "less than" },
        { value: "lessThanOrEqualTo", label: "less than or equal to" },
        { value: "inRange", label: "is between" },
        { value: "isEmpty", label: "is empty" },
        { value: "isNotEmpty", label: "is not empty" },
      ] satisfies Array<{ value: FilterOperator; label: string }>;
    case "date":
      return [
        { value: "equals", label: "is" },
        { value: "notEquals", label: "is not" },
        { value: "greaterThan", label: "is after" },
        { value: "greaterThanOrEqualTo", label: "is on or after" },
        { value: "lessThan", label: "is before" },
        { value: "lessThanOrEqualTo", label: "is on or before" },
        { value: "inRange", label: "is between" },
        { value: "isEmpty", label: "is empty" },
        { value: "isNotEmpty", label: "is not empty" },
      ] satisfies Array<{ value: FilterOperator; label: string }>;
    case "boolean":
    case "select":
      return [
        { value: "equals", label: "is" },
        { value: "notEquals", label: "is not" },
        { value: "isEmpty", label: "is empty" },
        { value: "isNotEmpty", label: "is not empty" },
      ] satisfies Array<{ value: FilterOperator; label: string }>;
    case "multi-select":
      return [
        { value: "equals", label: "contains" },
        { value: "notEquals", label: "does not contain" },
        { value: "isEmpty", label: "is empty" },
        { value: "isNotEmpty", label: "is not empty" },
      ] satisfies Array<{ value: FilterOperator; label: string }>;
    case "text":
    default:
      return [
        { value: "includesString", label: "contains" },
        { value: "notIncludesString", label: "does not contain" },
        { value: "equalsString", label: "equals" },
        { value: "notEqualsString", label: "does not equal" },
        { value: "startsWith", label: "starts with" },
        { value: "endsWith", label: "ends with" },
        { value: "isEmpty", label: "is empty" },
        { value: "isNotEmpty", label: "is not empty" },
      ] satisfies Array<{ value: FilterOperator; label: string }>;
  }
}

declare module "@tanstack/react-table" {
  interface FilterFns {
    fuzzy: FilterFn<TableFeatures, RowData>;
    dynamic: FilterFn<TableFeatures, RowData>;
  }

  interface FilterMeta {
    itemRank?: RankingInfo;
  }
}

export function fuzzyFilter<TFeatures extends TableFeatures, TData extends RowData>(
  row: Row<TFeatures, TData>,
  columnId: string,
  value: string,
  addMeta?: (meta: { itemRank: RankingInfo }) => void,
): boolean {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta?.({ itemRank });
  return itemRank.passed;
}

function isFalsy(value: unknown) {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

function toDate(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value !== "string") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isSameDay(left: Date, right: Date) {
  return left.toISOString().slice(0, 10) === right.toISOString().slice(0, 10);
}

const enhancedEquals: FilterFn<any, any> = (row, columnId, filterValue) => {
  const rowValue = row.getValue(columnId);

  if (Array.isArray(filterValue)) {
    return filterFn_arrIncludes(row, columnId, filterValue);
  }

  const rowDate = toDate(rowValue);
  const filterDate = toDate(filterValue);
  if (rowDate && filterDate) {
    return isSameDay(rowDate, filterDate);
  }

  return filterFn_equals(row, columnId, filterValue);
};

const compareDateOrValue = (
  row: Row<any, any>,
  columnId: string,
  filterValue: unknown,
  dateCompare: (rowDate: number, filterDate: number) => boolean,
  fallback: FilterFn<any, any>,
) => {
  const rowDate = toDate(row.getValue(columnId));
  const filterDate = toDate(filterValue);
  if (rowDate && filterDate) {
    return dateCompare(rowDate.getTime(), filterDate.getTime());
  }

  return fallback(row, columnId, filterValue);
};

const startsWith: FilterFn<any, any> = (row, columnId, filterValue) =>
  String(row.getValue(columnId) ?? "").toLowerCase().startsWith(String(filterValue).toLowerCase());

const endsWith: FilterFn<any, any> = (row, columnId, filterValue) =>
  String(row.getValue(columnId) ?? "").toLowerCase().endsWith(String(filterValue).toLowerCase());

const isEmpty: FilterFn<any, any> = (row, columnId) => isFalsy(row.getValue(columnId));

const inBetween: FilterFn<any, any> = (row, columnId, filterValue) => {
  if (!Array.isArray(filterValue)) return true;
  const [min, max] = filterValue;
  const rowValue = row.getValue(columnId);
  if (isFalsy(min) && isFalsy(max)) return true;

  const rowDate = toDate(rowValue);
  if (rowDate) {
    const minDate = toDate(min);
    const maxDate = toDate(max);
    return (!minDate || rowDate >= minDate) && (!maxDate || rowDate <= maxDate);
  }

  const numeric = Number(rowValue);
  if (Number.isFinite(numeric)) {
    return (isFalsy(min) || numeric >= Number(min)) && (isFalsy(max) || numeric <= Number(max));
  }

  return (isFalsy(min) || String(rowValue) >= String(min)) && (isFalsy(max) || String(rowValue) <= String(max));
};

const isRelativeToToday: FilterFn<any, any> = (row, columnId, filterValue) => {
  const rowDate = toDate(row.getValue(columnId));
  if (!rowDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  rowDate.setHours(0, 0, 0, 0);
  const diffInDays = Math.floor((rowDate.getTime() - today.getTime()) / 86_400_000);

  if (Array.isArray(filterValue)) {
    const [min, max] = filterValue.map((value) => Number(value));
    return (Number.isNaN(min) || diffInDays >= min) && (Number.isNaN(max) || diffInDays <= max);
  }

  return diffInDays === Number(filterValue);
};

export const dynamicFilterFn: FilterFn<any, any> = (row, columnId) => {
  const filters: ExtendedColumnFilter[] = row.table.store.state.columnFilters.filter((filter: ExtendedColumnFilter) => filter.id === columnId);
  if (filters.length === 0) return true;

  const joinOperator = filters[0]?.joinOperator ?? "and";

  return filters.reduce((passed: boolean, filter: ExtendedColumnFilter, index: number) => {
    const value = filter.value;
    const operator = filter.operator ?? "includesString";

    if (isFalsy(value) && operator !== "isEmpty" && operator !== "isNotEmpty") {
      return passed;
    }

    let result = true;
    switch (operator) {
      case "includesString":
        result = filterFn_includesString(row, columnId, value);
        break;
      case "notIncludesString":
        result = !filterFn_includesString(row, columnId, value);
        break;
      case "equalsString":
        result = filterFn_equalsString(row, columnId, value);
        break;
      case "notEqualsString":
        result = !filterFn_equalsString(row, columnId, value);
        break;
      case "equals":
        result = enhancedEquals(row, columnId, value);
        break;
      case "notEquals":
        result = !enhancedEquals(row, columnId, value);
        break;
      case "greaterThan":
        result = compareDateOrValue(row, columnId, value, (a, b) => a > b, filterFn_greaterThan);
        break;
      case "greaterThanOrEqualTo":
        result = compareDateOrValue(row, columnId, value, (a, b) => a >= b, filterFn_greaterThanOrEqualTo);
        break;
      case "lessThan":
        result = compareDateOrValue(row, columnId, value, (a, b) => a < b, filterFn_lessThan);
        break;
      case "lessThanOrEqualTo":
        result = compareDateOrValue(row, columnId, value, (a, b) => a <= b, filterFn_lessThanOrEqualTo);
        break;
      case "startsWith":
        result = startsWith(row, columnId, value);
        break;
      case "endsWith":
        result = endsWith(row, columnId, value);
        break;
      case "isEmpty":
        result = isEmpty(row, columnId, value);
        break;
      case "isNotEmpty":
        result = !isEmpty(row, columnId, value);
        break;
      case "inRange":
        result = inBetween(row, columnId, value);
        break;
      case "isRelativeToToday":
        result = isRelativeToToday(row, columnId, value);
        break;
    }

    if (index === 0) return result;
    return joinOperator === "and" ? passed && result : passed || result;
  }, true);
};
